import { HqlNode } from "../hql/types.js";

import { getAllNodesOfType } from "./calcAstUtils.js";
import { CalcParseNodeOrError } from "./CalcParser.js";

interface CalcExpr<T extends HqlNode | CalcParseNodeOrError> {
  alias: string;
  expr: T;
}

interface SortSuccess<T extends HqlNode | CalcParseNodeOrError> {
  type: "success";
  result: readonly CalcExpr<T>[];
}
interface SortFailure<T extends HqlNode | CalcParseNodeOrError> {
  type: "error";
  reason: string;
  partialResult?: readonly CalcExpr<T>[];
}
type SortResult<T extends HqlNode | CalcParseNodeOrError> =
  | SortSuccess<T>
  | SortFailure<T>;

/**
 * Sorts calcs topologically such that if calc Y depends on calc X,
 * then calc X will appear before calc Y in the returned list.
 */
export function sortCalcsTopologically<
  T extends HqlNode | CalcParseNodeOrError,
>(calcs: readonly CalcExpr<T>[]): SortResult<T> {
  if (calcs.length === 0) {
    return { type: "success", result: [] };
  }

  const aliasExprMap = new Map(calcs.map((calc) => [calc.alias, calc]));
  const exprDependenciesMap = new Map(
    calcs.map((calc) => {
      const deps = new Set(
        getAllNodesOfType(calc.expr, "column")
          .filter((n) => aliasExprMap.has(n.name))
          .map((n) => n.name),
      );
      return [calc.alias, deps];
    }),
  );

  const sortedAliases: string[] = [];

  // eslint-disable-next-line no-constant-condition -- we break inside the loop
  while (true) {
    // find calc that depends on no other calcs
    let emptyAlias: string | undefined;
    for (const [alias, deps] of exprDependenciesMap.entries()) {
      if (deps.size === 0) {
        emptyAlias = alias;
        break;
      }
    }

    if (emptyAlias == null) {
      const remainingAliases = Array.from(exprDependenciesMap.keys()).sort();
      // TODO: return a message with the actual cycle
      return {
        type: "error",
        reason: `Invalid cycle: Formulas reference each other: ${remainingAliases.join(
          ", ",
        )}`,
        partialResult: sortedAliases.map((alias) => aliasExprMap.get(alias)!),
      };
    }

    // add that calc to our ordered final list
    sortedAliases.push(emptyAlias);
    exprDependenciesMap.delete(emptyAlias);

    // if all calcs have been sorted, we're done
    if (exprDependenciesMap.size === 0) {
      break;
    }

    // for every remaining calc, remove the dependency on the calc we just added
    for (const deps of exprDependenciesMap.values()) {
      deps.delete(emptyAlias);
    }
  }

  return {
    type: "success",
    result: sortedAliases.map((alias) => aliasExprMap.get(alias)!),
  };
}
